import React, { useState, useEffect } from 'react';
import { Route, Switch, Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import AdminComments from './AdminComments';
import AdminPosts from './AdminPosts';
import AdminCategories from './AdminCategories';
import CreatePost from './admin/CreatePost';
import axios from 'axios';
import { getCategoryDisplayName } from '../utils/categoryUtils';
import './AdminDashboard.css';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend'
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [allComments, setAllComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();
  const location = useLocation();

  // Fetch data on component mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      fetchPosts();
      fetchPendingComments();
      fetchAllComments();
      // Set up polling for pending comments every 30 seconds
      const interval = setInterval(() => {
        fetchPendingComments();
        fetchAllComments();
      }, 30000);
      return () => clearInterval(interval);
    } else {
      // If no token, redirect to login
      history.push('/admin/login');
    }
  }, [history]);

  const fetchAllComments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        history.push('/admin/login');
        return;
      }

      const response = await api.get('/api/comments/all');
      setAllComments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching all comments:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        history.push('/admin/login');
      }
    }
  };

  const fetchPendingComments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        history.push('/admin/login');
        return;
      }

      const response = await api.get('/api/comments/pending');
      setPendingComments(response.data.data);
    } catch (error) {
      console.error('Error fetching pending comments:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        history.push('/admin/login');
      }
    }
  };

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        history.push('/admin/login');
        return;
      }

      const response = await api.get('/api/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        history.push('/admin/login');
      }
      setError(error.response?.data?.message || 'Error fetching posts');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    history.push('/admin/login');
  };

  const handleCreatePost = () => {
    history.push('/admin/posts/new');
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj post?')) {
      try {
        await api.delete(`/api/posts/${postId}`);
        setPosts(posts.filter(post => post._id !== postId));
      } catch (error) {
        console.error('Error deleting post:', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('adminToken');
          history.push('/admin/login');
        }
        setError(error.response?.data?.message || 'Error deleting post');
      }
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      await api.put(`/api/comments/${commentId}/approve`);
      fetchPendingComments();
      fetchAllComments();
    } catch (error) {
      console.error('Error approving comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj komentar?')) {
      try {
        await api.delete(`/api/comments/${commentId}`);
        fetchPendingComments();
        fetchAllComments();
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  if (loading) {
    return <div className="loading">Učitavanje...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Check if we're on a sub-route
  const isSubRoute = location.pathname !== '/admin' && location.pathname !== '/admin/dashboard';

  if (isSubRoute) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1>Admin Panel</h1>
          <div className="admin-actions">
            <button onClick={handleCreatePost} className="create-post-btn">
              Kreiraj novi post
            </button>
            <button onClick={handleLogout} className="logout-btn">
              Odjavi se
            </button>
          </div>
        </div>

        <nav className="admin-nav">
          <Link to="/admin/dashboard" className="nav-link">
            Dashboard
          </Link>
          <Link to="/admin/categories" className="nav-link">
            Kategorije
          </Link>
          <Link to="/admin/comments" className="nav-link">
            Komentari
            {pendingComments.length > 0 && (
              <span className="notification-badge">{pendingComments.length}</span>
            )}
          </Link>
        </nav>

        <div className="admin-content">
          <Switch>
            <Route exact path="/admin/posts/new" component={CreatePost} />
            <Route path="/admin/categories" component={AdminCategories} />
            <Route path="/admin/comments" component={AdminComments} />
          </Switch>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <div className="admin-actions">
          <button onClick={handleCreatePost} className="create-post-btn">
            Kreiraj novi post
          </button>
          <button onClick={handleLogout} className="logout-btn">
            Odjavi se
          </button>
        </div>
      </div>

      <nav className="admin-nav">
        <Link to="/admin/dashboard" className="nav-link active">
          Dashboard
        </Link>
        <Link to="/admin/categories" className="nav-link">
          Kategorije
        </Link>
        <Link to="/admin/comments" className="nav-link">
          Komentari
          {pendingComments.length > 0 && (
            <span className="notification-badge">{pendingComments.length}</span>
          )}
        </Link>
      </nav>

      <div className="admin-content">
        <div className="dashboard-overview">
          {/* Comments Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Komentari</h2>
              <Link to="/admin/comments" className="view-all-link">
                Pogledaj sve
              </Link>
            </div>
            
            {pendingComments.length > 0 && (
              <div className="pending-comments">
                <h3>Komentari na čekanju ({pendingComments.length})</h3>
                <div className="comments-list">
                  {pendingComments.slice(0, 3).map(comment => (
                    <div key={comment._id} className="comment-item pending">
                      <div className="comment-content">
                        <strong>{comment.author}</strong>
                        <p>{comment.content}</p>
                        <small>Na postu: {comment.postTitle}</small>
                      </div>
                      <div className="comment-actions">
                        <button 
                          onClick={() => handleApproveComment(comment._id)}
                          className="approve-btn"
                        >
                          Odobri
                        </button>
                        <button 
                          onClick={() => handleDeleteComment(comment._id)}
                          className="delete-btn"
                        >
                          Obriši
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="recent-comments">
              <h3>Najnoviji komentari</h3>
              <div className="comments-list">
                {allComments.slice(0, 5).map(comment => (
                  <div key={comment._id} className="comment-item">
                    <div className="comment-content">
                      <strong>{comment.author}</strong>
                      <p>{comment.content}</p>
                      <small>Na postu: {comment.postTitle} | {new Date(comment.createdAt).toLocaleDateString('sr-RS')}</small>
                    </div>
                    <div className="comment-actions">
                      <button 
                        onClick={() => handleDeleteComment(comment._id)}
                        className="delete-btn small"
                      >
                        Obriši
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Posts Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Svi postovi ({posts.length})</h2>
              <button onClick={handleCreatePost} className="create-post-btn">
                Kreiraj novi post
              </button>
            </div>
            
            <div className="posts-grid">
              {posts.map(post => (
                <div key={post._id} className="post-card">
                  {post.featuredImage && (
                    <img 
                      src={`${process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend'}${post.featuredImage}`} 
                      alt={post.title}
                      className="post-thumbnail"
                    />
                  )}
                  <div className="post-info">
                    <h3>{post.title}</h3>
                    <p className="post-excerpt">{post.excerpt || post.content.substring(0, 100) + '...'}</p>
                    <div className="post-meta">
                      <span className="post-date">
                        {new Date(post.createdAt).toLocaleDateString('sr-RS')}
                      </span>
                      <span className="post-category">{getCategoryDisplayName(post.categoryId)}</span>
                    </div>
                    <div className="post-actions">
                      <Link 
                        to={`/admin/posts/edit/${post._id}`}
                        className="edit-btn"
                      >
                        Izmeni
                      </Link>
                      <button
                        onClick={() => handleDeletePost(post._id)}
                        className="delete-btn"
                      >
                        Obriši
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Switch>
        <Route exact path="/admin">
          <Redirect to="/admin/dashboard" />
        </Route>
      </Switch>
    </div>
  );
};

export default AdminDashboard; 